
import { useState, useEffect, useCallback } from 'react';
import { Trip } from '@/types/itinerary';
import { useAuth } from '@/contexts/auth';
import { useTripFilters } from './hooks/useTripFilters';
import { useTripStats } from './hooks/useTripStats';
import { useTripSubscription } from './hooks/useTripSubscription';
import { fetchUserTrips } from './services/tripService';
import { TripFilters } from './hooks/tripTypes';

// Use 'export type' instead of 'export' for TypeScript types when isolatedModules is enabled
export type { TripFilters };

export const useTrips = () => {
  const { user } = useAuth();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Use the extracted hooks
  const { 
    filteredTrips, 
    searchTerm, 
    setSearchTerm, 
    filters, 
    setFilters, 
    resetFilters, 
    activeFilterCount 
  } = useTripFilters(trips);
  
  const stats = useTripStats(trips);
  
  // Define a callback for when trips need to be refreshed
  const handleTripsChange = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);
  
  // Set up realtime subscriptions
  useTripSubscription(user, handleTripsChange);

  // Effect to fetch user trips
  useEffect(() => {
    if (user) {
      const loadTrips = async () => {
        setIsLoading(true);
        const userTrips = await fetchUserTrips(user);
        setTrips(userTrips);
        setIsLoading(false);
      };
      
      loadTrips();
    }
  }, [user, refreshKey]);

  const handleTripDeleted = () => {
    console.log("Trip deleted callback triggered, refreshing trips list");
    // Force a re-fetch by incrementing the refresh key
    setRefreshKey(prev => prev + 1);
  };

  return {
    trips,
    filteredTrips,
    isLoading,
    searchTerm,
    setSearchTerm,
    handleTripDeleted,
    stats,
    filters,
    setFilters,
    resetFilters,
    activeFilterCount
  };
};
